/**
 * Mega Menu Navigation System
 * Handles desktop mega menu hover interactions and mobile drawer navigation
 */

class MegaMenu {
  constructor(navElement) {
    if (!navElement) return;

    this.navElement = navElement;
    this.activeMenuIndex = -1;
    this.hoverTimeout = null;
    this.debounceDelay = 0; // milliseconds - instant response

    this.init();
  }

  init() {
    const navLinks = this.navElement.querySelectorAll('.navigation__item');

    navLinks.forEach((item, index) => {
      const link = item.querySelector('.navigation__link');
      const megaMenu = item.querySelector('.mega-menu');

      if (!megaMenu) return; // Skip items without mega menu

      // Hover events with debounce
      item.addEventListener('mouseenter', () => this.openMenu(index));
      item.addEventListener('mouseleave', () => this.closeMenu());

      // Focus events for keyboard navigation (immediate, no debounce)
      link.addEventListener('focus', () => this.openMenuImmediate(index));

      // Escape key to close mega menu
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeMenuIndex !== -1) {
          this.closeMenu();
          link.focus(); // Return focus to parent link
        }
      });
    });
  }

  /**
   * Open mega menu with debounce delay (for hover intent)
   */
  openMenu(index) {
    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.activeMenuIndex = index;

      const navLinks = this.navElement.querySelectorAll('.navigation__item');
      navLinks.forEach((item, i) => {
        const link = item.querySelector('.navigation__link');
        const megaMenu = item.querySelector('.mega-menu');

        if (i === index) {
          megaMenu.classList.add('active');
          link.setAttribute('aria-expanded', 'true');
        } else {
          if (megaMenu) {
            megaMenu.classList.remove('active');
          }
          if (link.hasAttribute('aria-expanded')) {
            link.setAttribute('aria-expanded', 'false');
          }
        }
      });
    }, this.debounceDelay);
  }

  /**
   * Open mega menu immediately (for keyboard focus, no debounce)
   */
  openMenuImmediate(index) {
    clearTimeout(this.hoverTimeout);
    this.activeMenuIndex = index;

    const navLinks = this.navElement.querySelectorAll('.navigation__item');
    navLinks.forEach((item, i) => {
      const link = item.querySelector('.navigation__link');
      const megaMenu = item.querySelector('.mega-menu');

      if (i === index) {
        if (megaMenu) {
          megaMenu.classList.add('active');
        }
        link.setAttribute('aria-expanded', 'true');
      } else {
        if (megaMenu) {
          megaMenu.classList.remove('active');
        }
        if (link.hasAttribute('aria-expanded')) {
          link.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  /**
   * Close mega menu with debounce delay
   */
  closeMenu() {
    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.activeMenuIndex = -1;

      const navLinks = this.navElement.querySelectorAll('.navigation__item');
      navLinks.forEach((item) => {
        const link = item.querySelector('.navigation__link');
        const megaMenu = item.querySelector('.mega-menu');

        if (megaMenu) {
          megaMenu.classList.remove('active');
        }

        if (link.hasAttribute('aria-expanded')) {
          link.setAttribute('aria-expanded', 'false');
        }
      });
    }, this.debounceDelay);
  }
}

class MobileDrawer {
  constructor(drawerElement) {
    if (!drawerElement) return;

    this.drawerElement = drawerElement;
    this.drawerOpen = false;
    this.expandedAccordions = new Set();
    this.focusableElements = null;
    this.lastFocusedElement = null;

    this.init();
  }

  init() {
    const toggleButton = document.querySelector('[data-drawer-toggle]');
    const closeButton = this.drawerElement.querySelector('[data-drawer-close]');
    const overlay = document.querySelector('[data-drawer-overlay]');
    const accordions = this.drawerElement.querySelectorAll('.accordion');

    if (toggleButton) {
      toggleButton.addEventListener('click', () => this.toggle());
    }

    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    accordions.forEach((accordion, index) => {
      const trigger = accordion.querySelector('.accordion__trigger');
      if (trigger) {
        trigger.addEventListener('click', () => this.toggleAccordion(index));
      }
    });

    // Close drawer on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawerOpen) {
        this.close();
      }
    });

    // Close drawer if window crosses breakpoint
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 768 && this.drawerOpen) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.drawerOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.drawerOpen = true;
    this.lastFocusedElement = document.activeElement;

    this.drawerElement.classList.add('drawer-open');
    this.drawerElement.setAttribute('aria-hidden', 'false');

    const overlay = document.querySelector('[data-drawer-overlay]');
    if (overlay) {
      overlay.classList.add('active');
    }

    const toggleButton = document.querySelector('[data-drawer-toggle]');
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'true');
    }

    this.lockBodyScroll();
    this.trapFocus();

    // Focus first focusable element (close button)
    const firstFocusable = this.drawerElement.querySelector('[data-drawer-close]');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  close() {
    this.drawerOpen = false;

    this.drawerElement.classList.remove('drawer-open');
    this.drawerElement.setAttribute('aria-hidden', 'true');

    const overlay = document.querySelector('[data-drawer-overlay]');
    if (overlay) {
      overlay.classList.remove('active');
    }

    const toggleButton = document.querySelector('[data-drawer-toggle]');
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }

    this.unlockBodyScroll();
    this.expandedAccordions.clear();

    // Close all accordions
    const accordions = this.drawerElement.querySelectorAll('.accordion');
    accordions.forEach((accordion) => {
      const trigger = accordion.querySelector('.accordion__trigger');
      const content = accordion.querySelector('.accordion__content');

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      if (content) {
        content.setAttribute('hidden', '');
      }
    });

    // Restore focus to last focused element
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }

  toggleAccordion(index) {
    const accordions = this.drawerElement.querySelectorAll('.accordion');
    const accordion = accordions[index];
    if (!accordion) return;

    const trigger = accordion.querySelector('.accordion__trigger');
    const content = accordion.querySelector('.accordion__content');

    const isExpanded = this.expandedAccordions.has(index);

    if (isExpanded) {
      this.expandedAccordions.delete(index);
      trigger.setAttribute('aria-expanded', 'false');
      content.setAttribute('hidden', '');
    } else {
      this.expandedAccordions.add(index);
      trigger.setAttribute('aria-expanded', 'true');
      content.removeAttribute('hidden');
    }
  }

  trapFocus() {
    this.focusableElements = this.drawerElement.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    this.drawerElement.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  lockBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const navigation = document.querySelector('.navigation');
  const mobileDrawer = document.querySelector('#mobile-drawer');

  if (navigation) {
    new MegaMenu(navigation);
  }

  if (mobileDrawer) {
    new MobileDrawer(mobileDrawer);
  }
});
